import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import Draw, { createBox } from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Translate from "ol/interaction/Translate";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style, Circle as CircleStyle } from "ol/style";
import { transform } from "ol/proj";
import { getDistance } from "ol/sphere";
import { unByKey } from "ol/Observable";
import { clampToWebMercatorWorld, cloneFilter, roiPolygon4326 } from "@/services/layers/advancedFilters";

const roiStyle = new Style({
  stroke: new Stroke({ color: "#f97316", width: 3 }),
  fill: new Fill({ color: "rgba(249, 115, 22, 0.18)" }),
});

const handleStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: "#ffffff" }),
    stroke: new Stroke({ color: "#f97316", width: 3 }),
  }),
});

const to4326 = coordinate => transform(coordinate, "EPSG:3857", "EPSG:4326");
const to3857 = coordinate => transform(coordinate, "EPSG:4326", "EPSG:3857");

class AdvancedRoiService {
  constructor() {
    this.map = null;
    this.source = new VectorSource({ wrapX: false });
    this.handleSource = new VectorSource({ wrapX: false });
    this.layer = new VectorLayer({ source: this.source, style: roiStyle, zIndex: 10002 });
    this.handleLayer = new VectorLayer({ source: this.handleSource, style: handleStyle, zIndex: 10003 });
    this.draw = null;
    this.modify = null;
    this.translate = null;
    this.listenerKeys = [];
    this.spec = null;
    this.onChange = null;
    this.translatingHandle = null;
    this.translateStartSpec = null;
    this.popupSuppressionTimer = null;
  }

  init(map) {
    if (this.map === map) return;
    this.detach();
    this.map = map;
    this.ensureLayers();
  }

  ensureLayers() {
    if (!this.map) return;
    const layers = this.map.getLayers().getArray();
    if (!layers.includes(this.layer)) this.map.addLayer(this.layer);
    if (!layers.includes(this.handleLayer)) this.map.addLayer(this.handleLayer);
  }

  setChangeHandler(handler) {
    this.onChange = handler;
  }

  viewportSpec(operation = "intersects") {
    if (!this.map) return null;
    const extent = this.map.getView().calculateExtent(this.map.getSize());
    const southWest = to4326(clampToWebMercatorWorld([extent[0], extent[1]]));
    const northEast = to4326(clampToWebMercatorWorld([extent[2], extent[3]]));
    return {
      version: 1,
      source: "viewport",
      operation,
      crs: "EPSG:4326",
      shape: { kind: "viewport", bbox: [southWest[0], southWest[1], northEast[0], northEast[1]] },
    };
  }

  setSpec(spec, editable = true) {
    this.stopInteractions();
    this.spec = spec ? cloneFilter(spec) : null;
    this.render();
    if (editable && this.spec) this.enableEditing();
  }

  render() {
    this.ensureLayers();
    this.source.clear();
    this.handleSource.clear();
    if (!this.spec) return;
    const rings = roiPolygon4326(this.spec);
    if (!rings || !rings[0] || rings[0].length < 4) return;
    const coordinates = rings.map(ring => ring.map(to3857));
    this.source.addFeature(new Feature(new Polygon(coordinates)));
    this.buildHandles();
  }

  buildHandles() {
    this.handleSource.clear();
    if (!this.spec || !this.spec.shape) return;
    const shape = this.spec.shape;
    if (shape.kind === "rectangle" || shape.kind === "viewport") {
      const [west, south, east, north] = shape.bbox;
      [[west, south], [east, south], [east, north], [west, north]].forEach((coord, index) => {
        const feature = new Feature(new Point(to3857(coord)));
        feature.set("handleType", "corner");
        feature.set("handleIndex", index);
        this.handleSource.addFeature(feature);
      });
    } else if (shape.kind === "circle") {
      const center = shape.center;
      const ring = roiPolygon4326(this.spec)[0];
      const centerFeature = new Feature(new Point(to3857(center)));
      centerFeature.set("handleType", "center");
      const radiusFeature = new Feature(new Point(to3857(ring[0])));
      radiusFeature.set("handleType", "radius");
      this.handleSource.addFeatures([centerFeature, radiusFeature]);
    }
  }

  startDrawing(kind, operation = "intersects") {
    if (!this.map) return;
    this.stopInteractions();
    this.source.clear();
    this.handleSource.clear();
    const options = { source: this.source, type: kind === "rectangle" ? "Circle" : kind === "circle" ? "Circle" : "Polygon" };
    if (kind === "rectangle") options.geometryFunction = createBox();
    this.draw = new Draw(options);
    this.setPopupSuppressed(true);
    this.listenerKeys.push(this.draw.on("drawstart", () => this.source.clear()));
    this.listenerKeys.push(this.draw.on("drawend", event => {
      const geometry = event.feature.getGeometry();
      let shape;
      if (kind === "circle") {
        const center3857 = geometry.getCenter();
        const edge3857 = [center3857[0] + geometry.getRadius(), center3857[1]];
        shape = {
          kind: "circle",
          center: to4326(center3857),
          radiusMeters: getDistance(to4326(center3857), to4326(edge3857)),
          segments: 64,
        };
      } else if (kind === "rectangle") {
        const extent = geometry.getExtent();
        const sw = to4326([extent[0], extent[1]]);
        const ne = to4326([extent[2], extent[3]]);
        shape = { kind: "rectangle", bbox: [sw[0], sw[1], ne[0], ne[1]] };
      } else {
        const vertices = geometry.getCoordinates()[0].slice(0, -1).map(to4326);
        shape = { kind: "polygon", vertices };
      }
      this.spec = { version: 1, source: "draw", operation, crs: "EPSG:4326", shape };
      setTimeout(() => {
        this.map.removeInteraction(this.draw);
        this.draw = null;
        this.render();
        this.enableEditing();
        this.emitChange();
        this.releasePopupSuppression();
      }, 0);
    }));
    this.map.addInteraction(this.draw);
    this.map.getViewport().style.cursor = "crosshair";
  }

  enableEditing() {
    if (!this.map || !this.spec) return;
    const kind = this.spec.shape.kind;
    if (kind === "polygon") {
      this.modify = new Modify({ source: this.source, deleteCondition: () => false, insertVertexCondition: () => false });
      this.listenerKeys.push(this.modify.on("modifyend", event => {
        const feature = event.features.item(0);
        this.spec.shape.vertices = feature.getGeometry().getCoordinates()[0].slice(0, -1).map(to4326);
        this.emitChange();
      }));
      this.map.addInteraction(this.modify);
      return;
    }

    this.translate = new Translate({ layers: [this.handleLayer] });
    this.listenerKeys.push(this.translate.on("translatestart", event => {
      this.translatingHandle = event.features.item(0);
      this.translateStartSpec = cloneFilter(this.spec);
    }));
    this.listenerKeys.push(this.translate.on("translating", () => this.updateFromHandle(false)));
    this.listenerKeys.push(this.translate.on("translateend", () => this.updateFromHandle(true)));
    this.map.addInteraction(this.translate);
  }

  updateFromHandle(emit) {
    if (!this.translatingHandle || !this.translateStartSpec) return;
    const coordinate = to4326(this.translatingHandle.getGeometry().getCoordinates());
    const type = this.translatingHandle.get("handleType");
    const original = this.translateStartSpec.shape;
    if (type === "corner") {
      const corners = [
        [original.bbox[0], original.bbox[1]], [original.bbox[2], original.bbox[1]],
        [original.bbox[2], original.bbox[3]], [original.bbox[0], original.bbox[3]],
      ];
      const opposite = corners[(this.translatingHandle.get("handleIndex") + 2) % 4];
      this.spec.shape.bbox = [
        Math.min(coordinate[0], opposite[0]), Math.min(coordinate[1], opposite[1]),
        Math.max(coordinate[0], opposite[0]), Math.max(coordinate[1], opposite[1]),
      ];
    } else if (type === "center") {
      this.spec.shape.center = coordinate;
    } else if (type === "radius") {
      this.spec.shape.radiusMeters = getDistance(this.spec.shape.center, coordinate);
    }
    this.source.clear();
    const rings = roiPolygon4326(this.spec);
    this.source.addFeature(new Feature(new Polygon(rings.map(ring => ring.map(to3857)))));
    if (emit) {
      this.translatingHandle = null;
      this.translateStartSpec = null;
      this.render();
      this.stopInteractions();
      this.enableEditing();
      this.emitChange();
    }
  }

  emitChange() {
    if (this.onChange && this.spec) this.onChange(cloneFilter(this.spec));
  }

  stopInteractions() {
    if (this.draw && this.draw.abortDrawing) this.draw.abortDrawing();
    [this.draw, this.modify, this.translate].forEach(interaction => {
      if (interaction && this.map) this.map.removeInteraction(interaction);
    });
    this.draw = null;
    this.modify = null;
    this.translate = null;
    this.listenerKeys.forEach(key => unByKey(key));
    this.listenerKeys = [];
    if (this.map) this.map.getViewport().style.cursor = "";
    this.setPopupSuppressed(false);
  }

  setPopupSuppressed(suppressed) {
    if (this.popupSuppressionTimer) {
      clearTimeout(this.popupSuppressionTimer);
      this.popupSuppressionTimer = null;
    }
    if (this.map) this.map.set("suppressFeaturePopups", !!suppressed);
  }

  releasePopupSuppression() {
    if (!this.map) return;
    // OpenLayers emite singleclick después del drawend; se mantiene el bloqueo
    // brevemente para que el último vértice no identifique capas inferiores.
    this.popupSuppressionTimer = setTimeout(() => {
      if (this.map) this.map.set("suppressFeaturePopups", false);
      this.popupSuppressionTimer = null;
    }, 350);
  }

  clear() {
    this.stopInteractions();
    this.spec = null;
    this.source.clear();
    this.handleSource.clear();
  }

  detach() {
    this.stopInteractions();
    if (this.map) {
      this.map.removeLayer(this.layer);
      this.map.removeLayer(this.handleLayer);
    }
    this.map = null;
    this.clear();
  }
}

export const advancedRoiService = new AdvancedRoiService();
